import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { useApi } from '../../hooks/useApi';
import { hapticFeedback } from '../../utils/telegram';
import FormattedNumber from '../common/FormattedNumber/FormattedNumber';
import './SuccessPayments.css';

const SuccessPayments = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUserData } = useUser();
  const { processIPNCallback } = useApi();
  const [paymentData, setPaymentData] = useState(null);
  const [apiPaymentData, setApiPaymentData] = useState(null); // Данные из API
  const [isLoading, setIsLoading] = useState(true);
  const [ipnProcessed, setIpnProcessed] = useState(false);
  const [paymentNotFound, setPaymentNotFound] = useState(false);

  // Получаем параметры из URL
  const transactionId = searchParams.get('transaction_id');
  const paymentId = searchParams.get('payment_id');
  const npId = searchParams.get('NP_id'); // NowPayments ID
  const amount = searchParams.get('amount');
  const currency = searchParams.get('currency') || 'USDT';
  const status = searchParams.get('status');

  useEffect(() => {
    // Загружаем данные о платеже и обрабатываем IPN callback
    const loadPaymentData = async () => {
      try {
        let finalStatus = status || 'success';
        let ipnResult = null;

        // Всегда отправляем запрос на payments/ipn-callback при заходе на страницу
        if (!ipnProcessed) {
          try {
            console.log('Processing IPN callback with npId:', npId);
            ipnResult = await processIPNCallback(npId);
            console.log('IPN callback result:', ipnResult);
            
            // Проверяем, если платеж уже обработан или не найден
            if (ipnResult && ipnResult.alreadyProcessed) {
              console.log('Payment already processed or not found');
              setPaymentNotFound(true);
              setIpnProcessed(true);
              return; // Выходим из функции, не обрабатывая дальше
            }
            
            // Сохраняем данные из API
            if (ipnResult) {
              console.log('Saving API payment data:', ipnResult);
              setApiPaymentData(ipnResult);
            }
            
            // Обновляем статус на основе результата IPN
            if (ipnResult && ipnResult.success) {
              finalStatus = ipnResult.status || 'success';
            } else if (ipnResult && ipnResult.payment_status) {
              // Если получили данные напрямую от NowPayments API
              switch (ipnResult.payment_status) {
                case 'finished':
                case 'confirmed':
                  finalStatus = 'success';
                  break;
                case 'failed':
                case 'expired':
                  finalStatus = 'error';
                  break;
                default:
                  finalStatus = 'pending';
              }
            } else {
              finalStatus = 'error';
            }
            
            setIpnProcessed(true);
          } catch (ipnError) {
            console.error('Error processing IPN callback:', ipnError);
            finalStatus = 'error';
          }
        }

        // Формируем данные о платеже
        const paymentDataObj = {
          transactionId: transactionId || 'TXN' + Date.now(),
          paymentId: paymentId,
          npId: npId,
          amount: parseFloat(amount) || (apiPaymentData?.price_amount || 100.50),
          currency: currency || (apiPaymentData?.price_currency?.toUpperCase() || 'USDT'),
          status: finalStatus,
          timestamp: new Date(),
          type: 'deposit',
          ipnResult: ipnResult,
          apiData: apiPaymentData // Сохраняем полные данные из API
        };

        setPaymentData(paymentDataObj);

        // Обновляем данные пользователя после успешного платежа
        if (finalStatus === 'success') {
          await refreshUserData();
          hapticFeedback.success();
        } else {
          hapticFeedback.error();
        }

      } catch (error) {
        console.error('Error loading payment data:', error);
        setPaymentData({
          transactionId: transactionId || 'TXN' + Date.now(),
          paymentId: paymentId,
          npId: npId,
          amount: parseFloat(amount) || 0,
          currency: currency,
          status: 'error',
          timestamp: new Date(),
          type: 'deposit',
          error: error.message
        });
        hapticFeedback.error();
      } finally {
        setIsLoading(false);
      }
    };

    loadPaymentData();
  }, [transactionId, paymentId, npId, amount, currency, status, refreshUserData, processIPNCallback, ipnProcessed]);

  const handleContinue = () => {
    hapticFeedback.medium();
    navigate('/');
  };

  const handleViewTransactions = () => {
    hapticFeedback.light();
    navigate('/transactions');
  };

  if (isLoading) {
    return (
      <div className="success-payments">
        <div className="success-loading">
          <div className="loading-spinner"></div>
          <div className="loading-text">
            Обработка платежа через IPN...
          </div>
        </div>
      </div>
    );
  }

  // Если платеж не найден
  if (paymentNotFound) {
    return (
      <div className="success-payments">
        <div className="success-container">
          {/* Иконка информации */}
          <div className="status-icon info">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 16V12M12 8H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>

          {/* Заголовок */}
          <h1 className="success-title">Платеж не найден</h1>

          {/* Подзаголовок */}
          <p className="success-subtitle">
            Платеж с указанным ID не найден в системе или уже был обработан ранее.
          </p>

          {/* Информационная карточка */}
          <div className="payment-details-card">
            <div className="payment-details-header">
              <h3>Информация</h3>
            </div>
            
            {(npId || paymentId) && (
              <div className="payment-detail-row">
                <div className="detail-label">
                  {npId ? 'NowPayments ID' : 'ID платежа'}
                </div>
                <div className="detail-value payment-id">
                  {npId || paymentId}
                </div>
              </div>
            )}
            
            <div className="payment-detail-row">
              <div className="detail-label">Статус</div>
              <div className="detail-value status info">
                Платеж не найден
              </div>
            </div>
          </div>

          {/* Кнопка действия */}
          <div className="action-buttons">
            <button 
              className="action-button primary"
              onClick={handleContinue}
            >
              Перейти к торговле
            </button>
          </div>

          {/* Дополнительная информация */}
          <div className="success-info">
            <div className="info-item">
              <div className="info-icon">💡</div>
              <div className="info-text">
                Если вы считаете, что это ошибка, обратитесь в службу поддержки
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">📊</div>
              <div className="info-text">
                Вы можете продолжить торговлю с текущим балансом
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isSuccess = paymentData?.status === 'success';

  return (
    <div className="success-payments">
      <div className="success-container">
        {/* Иконка статуса */}
        <div className={`status-icon ${isSuccess ? 'success' : 'error'}`}>
          {isSuccess ? (
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>

        {/* Заголовок */}
        <h1 className="success-title">
          {isSuccess ? 'Платеж успешно выполнен!' : 'Ошибка платежа'}
        </h1>

        {/* Подзаголовок */}
        <p className="success-subtitle">
          {isSuccess 
            ? 'Ваш депозит был успешно зачислен на баланс. Средства уже доступны для торговли.'
            : 'Произошла ошибка при обработке платежа. Пожалуйста, попробуйте еще раз или обратитесь в поддержку.'
          }
        </p>

        {/* Детали платежа */}
        {paymentData && (
          <div className="payment-details-card">
            <div className="payment-details-header">
              <h3>Детали транзакции</h3>
            </div>
            
            <div className="payment-detail-row">
              <div className="detail-label">Сумма</div>
              <div className="detail-value">
                <FormattedNumber value={paymentData.amount} suffix={paymentData.currency} />
              </div>
            </div>
            
            <div className="payment-detail-row">
              <div className="detail-label">ID транзакции</div>
              <div className="detail-value transaction-id">{paymentData.transactionId}</div>
            </div>
            
            {paymentData.npId && (
              <div className="payment-detail-row">
                <div className="detail-label">NowPayments ID</div>
                <div className="detail-value payment-id">{paymentData.npId}</div>
              </div>
            )}
            
            {paymentData.paymentId && !paymentData.npId && (
              <div className="payment-detail-row">
                <div className="detail-label">ID платежа</div>
                <div className="detail-value payment-id">{paymentData.paymentId}</div>
              </div>
            )}
            
            <div className="payment-detail-row">
              <div className="detail-label">Дата и время</div>
              <div className="detail-value">
                {paymentData.timestamp.toLocaleDateString('ru-RU')} в {paymentData.timestamp.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
            
            <div className="payment-detail-row">
              <div className="detail-label">Статус</div>
              <div className={`detail-value status ${paymentData.status}`}>
                {paymentData.status === 'success' ? 'Успешно' : 'Ошибка'}
              </div>
            </div>
            
            <div className="payment-detail-row">
              <div className="detail-label">Тип операции</div>
              <div className="detail-value">
                {paymentData.type === 'deposit' ? 'Пополнение' : 'Вывод'}
              </div>
            </div>
            
            {paymentData.ipnResult && (
              <div className="payment-detail-row">
                <div className="detail-label">Обработка платежа</div>
                <div className="detail-value">
                  {paymentData.ipnResult.success ? 'Успешно обработан' : 'Ошибка обработки'}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Подробные данные из API */}
        {paymentData?.apiData && (
          <div className="payment-details-card">
            <div className="payment-details-header">
              <h3>Подробные данные платежа</h3>
            </div>
            
            {paymentData.apiData.actually_paid && (
              <div className="payment-detail-row">
                <div className="detail-label">Фактически оплачено</div>
                <div className="detail-value">
                  {paymentData.apiData.actually_paid} {paymentData.apiData.pay_currency?.toUpperCase()}
                </div>
              </div>
            )}
            
            {paymentData.apiData.outcome_amount && (
              <div className="payment-detail-row">
                <div className="detail-label">Получено</div>
                <div className="detail-value">
                  {paymentData.apiData.outcome_amount} {paymentData.apiData.outcome_currency?.toUpperCase()}
                </div>
              </div>
            )}
            
            {paymentData.apiData.pay_address && (
              <div className="payment-detail-row">
                <div className="detail-label">Адрес оплаты</div>
                <div className="detail-value payment-id">{paymentData.apiData.pay_address}</div>
              </div>
            )}
            
            {paymentData.apiData.payin_hash && (
              <div className="payment-detail-row">
                <div className="detail-label">Hash входящей транзакции</div>
                <div className="detail-value payment-id">{paymentData.apiData.payin_hash}</div>
              </div>
            )}
            
            {paymentData.apiData.payout_hash && (
              <div className="payment-detail-row">
                <div className="detail-label">Hash исходящей транзакции</div>
                <div className="detail-value payment-id">{paymentData.apiData.payout_hash}</div>
              </div>
            )}
            
            {paymentData.apiData.created_at && (
              <div className="payment-detail-row">
                <div className="detail-label">Создан</div>
                <div className="detail-value">
                  {new Date(paymentData.apiData.created_at).toLocaleDateString('ru-RU')} в {new Date(paymentData.apiData.created_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            )}
            
            {paymentData.apiData.updated_at && (
              <div className="payment-detail-row">
                <div className="detail-label">Обновлен</div>
                <div className="detail-value">
                  {new Date(paymentData.apiData.updated_at).toLocaleDateString('ru-RU')} в {new Date(paymentData.apiData.updated_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            )}
            
            {paymentData.apiData.type && (
              <div className="payment-detail-row">
                <div className="detail-label">Тип операции</div>
                <div className="detail-value">{paymentData.apiData.type}</div>
              </div>
            )}
          </div>
        )}

        {/* Обновленный баланс */}
        {isSuccess && user && (
          <div className="balance-update-card">
            <div className="balance-update-header">
              <h3>Ваш баланс</h3>
            </div>
            <div className="current-balance">
              <div className="balance-label">Текущий баланс</div>
              <div className="balance-amount">
                <FormattedNumber value={user.balance} suffix="USDT" />
              </div>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="action-buttons">
          <button 
            className="action-button primary"
            onClick={handleContinue}
          >
            {isSuccess ? 'Продолжить торговлю' : 'Вернуться на главную'}
          </button>
          
          {isSuccess && (
            <button 
              className="action-button secondary"
              onClick={handleViewTransactions}
            >
              Посмотреть транзакции
            </button>
          )}
        </div>

        {/* Дополнительная информация */}
        {isSuccess && (
          <div className="success-info">
            <div className="info-item">
              <div className="info-icon">💡</div>
              <div className="info-text">
                Средства зачислены мгновенно и готовы к использованию
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">🔒</div>
              <div className="info-text">
                Все транзакции защищены и проходят через безопасные каналы
              </div>
            </div>
            <div className="info-item">
              <div className="info-icon">📊</div>
              <div className="info-text">
                Начните торговлю прямо сейчас и получайте прибыль от арбитража
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuccessPayments; 